import {debounce} from '@genestack/ui';
import React from 'react';

import {useSearchDataFromUrl} from '../../../explore-view/hooks/use-search-data-from-url';
import {useGlobalStateVariable, Variables} from '../../../../../providers/global-state';
import {SearchData, SearchSuggestion} from '../interface';
import {
    isSearchCategory,
    isSearchByDocNameSuggestion,
    isSearchNode,
    isPrefixSuggestion,
    isHistoryRecordSuggestion,
    isSearchByDocName,
    isSearchByMetadata,
    getMetadataFieldLabel
} from '../suggestion-helpers';
import {CATEGORY_SEARCH_PREFIX, DOC_NAME_SEARCH_PREFIX} from './use-suggestions-props';
import {convertHistoryRecordToSearchData, getHistoryRecordType} from '../../../explore-view/utils';

const DEBOUNCE_DELAY = 500;

export interface SidebarLoadingState {
    isLoading: boolean;
    isLoadingNextPage: boolean;
}

interface Props {
    useDefaultSearch?: typeof useSearchDataFromUrl;
    onSearch?: (data: SearchData) => void;
    searchDataGlobalStateVar: keyof Variables;
}

function getInputValueFromSearchData(searchData?: SearchData) {
    if (!searchData) {
        return '';
    }

    if (isSearchNode(searchData) || isSearchCategory(searchData)) {
        return searchData.name;
    }

    if (isSearchByMetadata(searchData)) {
        return searchData.displayName || searchData.value;
    }

    return searchData.searchString;
}

function getInputPrefixFromSearchData(searchData?: SearchData) {
    if (!searchData) {
        return '';
    }

    if (isSearchCategory(searchData)) {
        return CATEGORY_SEARCH_PREFIX;
    }

    if (isSearchByDocName(searchData)) {
        return DOC_NAME_SEARCH_PREFIX;
    }

    if (isSearchByMetadata(searchData)) {
        return getMetadataFieldLabel(searchData.fieldType);
    }

    return searchData.nodeType;
}

export const useSidebarSearchProps = ({
    useDefaultSearch,
    onSearch,
    searchDataGlobalStateVar
}: Props) => {
    // search data is used to query search results from BE
    const [searchDataState, setSearchDataState] = useGlobalStateVariable(
        searchDataGlobalStateVar,
        undefined
    ) as [SearchData | undefined, (value: SearchData | undefined) => void];

    const searchData =
        // eslint-disable-next-line react-hooks/rules-of-hooks
        (useDefaultSearch && useDefaultSearch({setSearchData: setSearchDataState})) ||
        searchDataState;

    const initialInputValue = getInputValueFromSearchData(searchData);
    const initialInputPrefix = getInputPrefixFromSearchData(searchData);

    const [inputValue, setInputValue] = React.useState<string>(initialInputValue);
    const [inputPrefix, setInputPrefix] = React.useState<string>(initialInputPrefix);

    const [isOpen, setOpenStatus] = React.useState<boolean>(false);

    const handleOpenSuggestions = React.useCallback(() => {
        setOpenStatus(true);
    }, [setOpenStatus]);

    const handleCloseSuggestions = React.useCallback(() => {
        setOpenStatus(false);
    }, [setOpenStatus]);

    // stores input value before the prefix was selected so that on clear btn click we can revert to it
    const [previousInputValue, setPreviousInputValue] = React.useState<string>('');

    const search = React.useCallback(
        (nextSearchData: SearchData) => {
            setSearchDataState(nextSearchData);
            setInputValue(getInputValueFromSearchData(nextSearchData));
            setInputPrefix(getInputPrefixFromSearchData(nextSearchData));

            if (onSearch) {
                onSearch(nextSearchData);
            }
        },
        [setSearchDataState, onSearch]
    );

    const handleSuggestionSelected = React.useCallback(
        (suggestion: SearchSuggestion) => {
            setPreviousInputValue(inputValue);

            if (isPrefixSuggestion(suggestion)) {
                setInputValue(``);
                setInputPrefix(suggestion.prefix);
                return;
            }

            if (isHistoryRecordSuggestion(suggestion)) {
                search(convertHistoryRecordToSearchData(suggestion));
                setInputPrefix(getHistoryRecordType(suggestion));
                return;
            }

            if (isSearchByDocNameSuggestion(suggestion)) {
                search({...suggestion, searchString: inputValue});
            } else {
                search(suggestion);
            }
        },
        [search, inputValue]
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const deferredSearchDocument = React.useCallback(
        debounce((value: string) => {
            setSearchDataState({type: 'document', searchString: value});
        }, DEBOUNCE_DELAY),
        []
    );

    const onInputValueChange = React.useCallback(
        (value: string) => {
            setInputValue(value);

            if (inputPrefix && previousInputValue) {
                setPreviousInputValue('');
            }

            if (inputPrefix === DOC_NAME_SEARCH_PREFIX) {
                deferredSearchDocument(value);
            } else {
                handleOpenSuggestions();
            }
        },
        [deferredSearchDocument, inputPrefix, previousInputValue, handleOpenSuggestions]
    );

    const onClearClick = React.useCallback(() => {
        setSearchDataState(undefined);
        handleOpenSuggestions();

        if (!inputPrefix) {
            setInputValue('');
            return;
        }

        if (inputPrefix && previousInputValue) {
            setInputPrefix('');
            setInputValue(previousInputValue);
            setPreviousInputValue('');
            return;
        }

        if (inputPrefix && !previousInputValue) {
            setInputPrefix('');
            return;
        }
    }, [inputPrefix, previousInputValue, setSearchDataState, handleOpenSuggestions]);

    const onPrefixDeleted = React.useCallback(() => {
        setSearchDataState(undefined);

        setInputPrefix('');
        handleOpenSuggestions();
    }, [setSearchDataState, handleOpenSuggestions]);

    return {
        searchData,
        search,
        onClearClick,
        onInputValueChange,
        handleSuggestionSelected,
        inputValue,
        inputPrefix,
        onPrefixDeleted,
        isOpen,
        handleOpenSuggestions,
        handleCloseSuggestions
    };
};
